import { BehaviorSubject, Observable, tap } from 'rxjs';
import { Injectable } from '@angular/core';
import { ApiResponse, ApiService } from './api.service';
import { Order } from '@models/order.model';
import { User } from '@sentry/angular';

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  ordersCache = new BehaviorSubject<Order[] | undefined>(undefined);

  constructor(
    private apiService: ApiService
  ) { }

  get orders() {
    return this.ordersCache.asObservable();
  }

  setOrders(orders: Order[] | undefined) {
    this.ordersCache.next(orders);
  }

  addOrder(order: Order) {
    const orders = this.ordersCache.value;
    orders?.push(order);
    this.setOrders(orders);
  }

  getOrders(): Observable<ApiResponse<User>> | void {
    if (this.ordersCache.value) {
      return;
    }

    return this.apiService.get<ApiResponse<User>>('me', { load: ['orders.event', 'orders.promoCode'] }).pipe(tap({
      next: (response: any) => {
        this.setOrders(response.data.orders);
      }
    }));
  }

  getOrderFromApi(id: number, params: any = {}): Observable<ApiResponse<Order>> {
    return this.apiService.get<ApiResponse<Order>>(`orders/${id}`, params);
  }

  get(id: number): Order | null | undefined {
    return this.ordersCache.value?.find((o: Order) => o.id === id);
  }

  paymentLinkInfo(id: number, signature: string, params: any = {}): Observable<ApiResponse<Order>> {
    params.signature = signature;
    return this.apiService.get<ApiResponse<Order>>(`payment-links/orders/${id}`, params);
  }

  createPaymentIntent(id: number, signature: string, params: any = {}): Observable<ApiResponse<any>> {
    return this.apiService.post<ApiResponse<any>>(`payment-links/orders/${id}/payment-intent?signature=${signature}`, params);
  }

  completePayment(id: number, signature: string, params: any = {}): Observable<ApiResponse<any>> {
    return this.apiService.post<ApiResponse<any>>(`payment-links/orders/${id}/complete?signature=${signature}`, params);
  }

  downloadOrderPdf(id: number) {
    return this.apiService.getFile(`orders/${id}/download-pdfs`);
  }

  downloadTicketPdf(id: number) {
    return this.apiService.getFile(`tickets/${id}/download-pdf`);
  }
}
